<script setup lang="ts">
import { ref, onMounted } from 'vue'
import AppModal from '@/components/ui/AppModal.vue'
import { useFormsStore } from '@/domains/home/services/nir/configurations/stores/forms.ts'
import type { EntityType } from '@/domains/home/services/nir/configurations/types/forms.ts'
import { dateTimeFormat } from '@/commons/composables/transformations.ts'

const loading = ref(false)
const saving = ref(false)
const modalOpen = ref(false)
const isEdit = ref(false)
const form = ref({
  entityTypeId: '',
  templateName: '',
})

const store = useFormsStore()

onMounted(() => {
  fetch()
})

function fetch() {
  loading.value = true
  store.fetchPrimaryEntityTypes().finally(() => {
    loading.value = false
  })
}

function openAdd(item: EntityType) {
  isEdit.value = false
  form.value = { entityTypeId: item.id, templateName: '' }
  modalOpen.value = true
}

function openEdit(item: EntityType) {
  isEdit.value = true
  form.value = { entityTypeId: item.id, templateName: item.regulationFormTemplate || '' }
  modalOpen.value = true
}

function closeModal() {
  modalOpen.value = false
  form.value = { entityTypeId: '', templateName: '' }
}

function submit() {
  saving.value = true
  const action = isEdit.value
    ? store.updateRegulationFormTemplate(form.value)
    : store.addRegulationFormTemplate(form.value)

  action
    .then(() => {
      saving.value = false
      modalOpen.value = false
      fetch()
    })
    .catch(() => {
      saving.value = false
    })
}
</script>

<template>
  <div class="flex-1 flex flex-col">
    <!-- Primary Entity Types Table -->
    <div class="bg-white rounded shadow flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Name
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Template Name
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Date
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in store.primaryEntityTypes"
              :key="item.id"
              class="hover:bg-gray-50 transition"
            >
              <td class="py-3 px-4 border-b border-gray-200">
                <span class="text-gray-900 font-medium">{{ item.name }}</span>
              </td>
              <td class="py-3 px-4 border-b border-gray-200">
                <div class="flex items-center space-x-2">
                  <span class="text-gray-600">
                    {{ item.regulationFormTemplate || '--' }}
                  </span>
                  <button
                    v-if="!item.regulationFormTemplate"
                    @click="openAdd(item)"
                    class="btn-sm-primary"
                    title="Add Template"
                  >
                    <i class="fa-solid fa-plus"></i>
                  </button>
                  <button
                    v-else
                    @click="openEdit(item)"
                    class="btn-sm-primary"
                    title="Edit Template"
                  >
                    <i class="fa-solid fa-edit"></i>
                  </button>
                </div>
              </td>
              <td class="py-3 px-4 border-b border-gray-200 text-gray-400">
                {{ dateTimeFormat(item.createdAt) }}
              </td>
            </tr>
            <!-- Empty State -->
            <tr v-if="!loading && (!store.primaryEntityTypes || store.primaryEntityTypes.length === 0)">
              <td colspan="4" class="py-12 text-center">
                <div class="flex flex-col items-center justify-center text-gray-500">
                  <i class="fa-solid fa-file-lines text-4xl mb-3 text-gray-300"></i>
                  <p class="text-lg font-medium">No primary entity types found</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Add/Edit Template Modal -->
    <AppModal v-model="modalOpen" md>
      <div class="flex flex-col p-6 bg-white">
        <h2 class="text-lg font-bold text-gray-900 mb-4">
          {{ isEdit ? 'Edit Regulation Form Template' : 'Add Regulation Form Template' }}
        </h2>

        <form @submit.prevent="submit" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              <i class="fa-solid fa-file-lines text-gray-400 mr-1"></i>
              Template Name *
            </label>
            <input
              v-model="form.templateName"
              type="text"
              required
              placeholder="Enter template name"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button type="button" @click="closeModal" class="btn-danger">
              <i class="fas fa-times mr-1"></i>
              Cancel
            </button>
            <button
              type="submit"
              :disabled="saving"
              class="btn-primary px-4 py-2 bg-primary hover:bg-primary-700 text-white rounded text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fa-solid fa-check mr-1"></i>
              <span v-if="!saving">{{ isEdit ? 'Update Template' : 'Create Template' }}</span>
              <span v-else>Saving...</span>
            </button>
          </div>
        </form>
      </div>
    </AppModal>
  </div>
</template>

<style scoped>
table {
  border-collapse: separate;
  border-spacing: 0;
}
</style>